import React from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { gql } from '@soundxyz/gql-string';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { ArtistHighlightsFragmentDoc, getFragment } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ArtistHighlights on ArtistPartnership {
    id
    highlights {
      imageLabel
      imageUrl
      linkUrl
    }
  }
`);

export const ArtistHighlight = ({
  highlightFrag,
  hideLast,
  isAboveFold = false,
}: {
  highlightFrag: FragmentType<ArtistHighlightsFragmentDoc>;
  hideLast?: boolean;
  isAboveFold?: boolean;
}) => {
  const MAX_ARTISTS = 8;

  const { highlights } = getFragment(ArtistHighlightsFragmentDoc, highlightFrag);

  if (!highlights || highlights.length === 0) {
    return null;
  }

  const artists = hideLast ? highlights.slice(0, MAX_ARTISTS - 1) : highlights;

  return (
    <div
      className={clsx('grid grid-rows-5', hideLast ? 'grid-cols-10' : 'grid-cols-11')}
      style={{
        gridTemplateAreas: `
      ". . . . . . . . . . a7"
      ". . . a3 a3 . . a6 a6 a6 ."
      ". a1 . a3 a3 . . a6 a6 a6 ."
      "a0 . a2 . . a4 . a6 a6 a6 ."
      ". . . . . . a5 . . . ."
      `,
      }}
    >
      {artists?.map((highlight, index) => {
        const IS_PRIORITY_IMAGE = isAboveFold && index === 6 ? true : false;

        return (
          <div
            key={index}
            style={{ gridArea: `a${index}`, transform: 'translateZ(0)' }}
            className={clsx(
              //hover image scale
              'relative  flex aspect-square h-9 w-9 overflow-hidden rounded-md transition-all sm:h-11 sm:w-11 lg:h-12 lg:w-12   2xl:h-14 2xl:w-14',
              index === 0 &&
                '!h-6 !w-6 self-start justify-self-end sm:!h-8 sm:!w-8 2xl:!h-10 2xl:!w-10',
              index === 3 &&
                '!h-16 !w-16 self-end justify-self-center md:!h-24 md:!w-24  2xl:!h-28 2xl:!w-28',
              index === 6 &&
                '!h-28 !w-28 self-center justify-self-center md:!h-32 md:!w-32 lg:!h-40 lg:!w-40 2xl:!h-44 2xl:!w-44',
            )}
          >
            <AccessibleLink
              pathnameUri={webappUriPath(highlight.linkUrl)}
              className="transition-transform duration-500 ease-in-out hover:[&>img]:scale-110 "
            >
              <Image
                src={highlight.imageUrl}
                alt={highlight.imageLabel}
                fill
                sizes="176px"
                priority={IS_PRIORITY_IMAGE}
                className="transition-all"
              />
            </AccessibleLink>
          </div>
        );
      })}
    </div>
  );
};
